/**
 * Utility functions for handling UTM parameters
 */

export const PLATFORM_HOSTNAME = "worldclassedge.app";

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  fbclid?: string;
  adset_id?: string;
  ad_id?: string;
  placement?: string;
}

/**
 * Returns true when the given URL points to the platform domain.
 * Safe for relative or invalid URLs (returns false).
 */
export function isPlatformUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return (
      hostname === PLATFORM_HOSTNAME ||
      hostname.endsWith(`.${PLATFORM_HOSTNAME}`)
    );
  } catch {
    return false;
  }
}

/**
 * Extracts UTM parameters from the current URL search params
 * This function works on the client side only
 */
export function getUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'fbclid',
    'adset_id',
    'ad_id',
    'placement',
  ] as const;
  
  utmKeys.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
}

/**
 * Appends UTM parameters to a given URL
 */
export function appendUTMParams(baseUrl: string, utmParams: UTMParams): string {
  const url = new URL(baseUrl);
  
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  
  return url.toString();
}

/**
 * Gets UTM parameters from current URL and appends them to a target URL
 * This is a convenience function that combines both operations
 * Only adds UTM parameters that are not already present in the target URL
 */
export function forwardUTMParams(targetUrl: string): string {
  const currentUTMParams = getUTMParams();
  
  // If no UTM parameters in current URL, return original URL
  if (Object.keys(currentUTMParams).length === 0) {
    return targetUrl;
  }
  
  const url = new URL(targetUrl);
  
  // Only add UTM parameters that are not already present in the target URL
  Object.entries(currentUTMParams).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });
  
  return url.toString();
}